import React, { useEffect, useMemo, useState } from "react";
import {
  AppProvider,
  Checkbox,
  Modal,
  ResourceItem,
  ResourceList,
  InlineStack,
  Text,
  Box,
  TextField,
} from "@shopify/polaris";
import PropTypes from "prop-types";
import { withErrorBoundary } from "lib/hoc";

const AddVendor = (props) => {
  const {
    activator,
    active,
    cms,
    handleChange,
    vendorList,
    userInput,
    setUserInput,
    standardAccess,
    setVendorList,
    setDisable,
  } = props;

  const [searchValue, setSearchValue] = useState("");
  const [tempVendorList, setTempVendorList] = useState([]);

  useEffect(() => {
    const checkedIds = (vendorList || []).filter((v) => v?.isCheck).map((v) => v._id);
    setTempVendorList(checkedIds);
  }, [vendorList]);

  const filteredVendors = useMemo(() => {
    const q = (searchValue || "").trim().toLowerCase();
    if (!q) return vendorList || [];
    return (vendorList || []).filter((item) => {
      const code = (item?._id || "").toLowerCase();
      const brand = (item?.brandName || "").toLowerCase();
      return code.includes(q) || brand.includes(q);
    });
  }, [vendorList, searchValue]);

  const handleSearchChange = (value) => setSearchValue(value);

  const handleCheckbox = (checked, id) => {
    setVendorList((prev = []) => {
      const next = prev.map((v) => (v._id === id ? { ...v, isCheck: checked } : v));
      const nextChecked = next.filter((v) => v.isCheck).map((v) => v._id);
      setTempVendorList(nextChecked);
      return next;
    });
    setDisable(false);
  };

  const onSubmit = () => {
    const checkedIds = new Set((vendorList || []).filter((v) => v?.isCheck).map((v) => v._id));

    const next = (Array.isArray(userInput) ? userInput : []).filter((entry) => checkedIds.has(entry.vendorId));

    (vendorList || []).forEach((v) => {
      if (checkedIds.has(v._id) && !next.find((e) => e.vendorId === v._id)) {
        next.push({
          vendorId: v._id,
          product: { ...standardAccess.product },
          order: { ...standardAccess.order },
          payment: { ...standardAccess.payment },
        });
      }
    });

    setUserInput(next);
    setDisable(false);
    handleChange();
  };

  const renderItem = (item) => {
    const { _id: id, brandName, isCheck } = item;
    return (
      <ResourceItem id={id} key={id}>
        <InlineStack blockAlign="center">
          <Checkbox checked={!!isCheck} onChange={(checked) => handleCheckbox(checked, id)} />
          <Text as="span">{brandName}</Text>
        </InlineStack>
      </ResourceItem>
    );
  };

  return (
    <AppProvider i18n={{}}>
      <Modal
        activator={activator}
        open={active}
        onClose={handleChange}
        title={cms("label.addVendor")}
        primaryAction={{
          content: cms("button.addVendor"),
          onAction: onSubmit,
        }}
        secondaryActions={[
          {
            content: cms("common.button.cancel"),
            onAction: handleChange,
          },
        ]}
      >
        <Modal.Section>
          <Box>
            <TextField onChange={handleSearchChange} value={searchValue} placeholder="Search Vendors" />
          </Box>
          <br />
          <ResourceList items={filteredVendors} renderItem={renderItem} />
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
};

AddVendor.propTypes = {
  active: PropTypes.bool,
  activator: PropTypes.any,
  cms: PropTypes.func,
  handleChange: PropTypes.func,
  vendorList: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      brandName: PropTypes.string,
      isCheck: PropTypes.bool,
    })
  ),
  userInput: PropTypes.arrayOf(
    PropTypes.shape({
      vendorId: PropTypes.string.isRequired,
      product: PropTypes.object,
      order: PropTypes.object,
      payment: PropTypes.object,
    })
  ),
  setUserInput: PropTypes.func,
  standardAccess: PropTypes.shape({
    product: PropTypes.object.isRequired,
    order: PropTypes.object.isRequired,
    payment: PropTypes.object.isRequired,
  }).isRequired,
  setVendorList: PropTypes.func,
  setDisable: PropTypes.func,
};

AddVendor.defaultProps = {
  active: false,
  activator: null,
  cms: () => (k) => k,
  handleChange: () => {},
  vendorList: [],
  userInput: [],
  setUserInput: () => {},
  setVendorList: () => {},
  setDisable: () => {},
};

export default withErrorBoundary(AddVendor);
